import { withEntityTitle } from '@/components/workflows/docProvenance';
import { sealBlockHtml, SEAL_STYLE_CSS, type SealContext } from '@/components/workflows/sealModel';
import type { SigningMode } from '@/components/workflows/signingModel';
import { APPLICATION_FORMATS_APPROVED } from './flags';

/**
 * DRAFT document builders for Member Application Links.
 *
 * Plain spoken register, no dashes, no internal codes, and no promises about
 * outcomes. Every page is marked DRAFT while APPLICATION_FORMATS_APPROVED is false.
 */

function esc(value: unknown): string {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

function longDate(value?: string | Date): string {
  const d = value ? new Date(value) : new Date();
  const safe = Number.isNaN(d.getTime()) ? new Date() : d;
  return safe.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
}

const BASE_STYLE = `
  @page { size: letter; margin: 1in; }
  body { font-family: Cambria, Georgia, serif; font-size: 12pt; line-height: 1.6; color: #1a1a1a; }
  h1 { font-size: 16pt; text-align: center; text-transform: uppercase; letter-spacing: 0.06em; margin-bottom: 4px; }
  h2 { font-size: 12pt; text-transform: uppercase; letter-spacing: 0.05em; margin-top: 26px; }
  .subtitle { text-align: center; font-style: italic; margin-bottom: 26px; }
  .draft { border: 1px solid #8a6d3b; background: #fdf6e3; color: #6b5320; padding: 10px 14px; margin-bottom: 22px; font-size: 10pt; text-align: center; letter-spacing: 0.04em; }
  .sig { margin-top: 46px; }
  .sig-line { border-bottom: 1px solid #333; width: 320px; height: 34px; margin-bottom: 4px; }
  .sig-label { font-size: 10pt; }
  .note { font-size: 10pt; font-style: italic; color: #444; margin-top: 26px; }
  ul li, ol li { margin-bottom: 8px; }
  ${SEAL_STYLE_CSS}
`;

const DRAFT_BANNER =
  '<div class="draft">DRAFT format. This wording has not yet been approved. Do not sign this page until the approved format is in place.</div>';

function page(title: string, subtitle: string, inner: string): string {
  return `<!DOCTYPE html>
<html lang="en">
<head><meta charset="UTF-8"><title>${esc(title)}</title><style>${BASE_STYLE}</style></head>
<body>
  ${APPLICATION_FORMATS_APPROVED ? '' : DRAFT_BANNER}
  <h1>${esc(title)}</h1>
  <p class="subtitle">${esc(subtitle)}</p>
  ${inner}
  <p class="note">Papers sign before anything moves. The date on this page is the true date it is signed.</p>
</body>
</html>`;
}

function sigBlock(label: string): string {
  return `<div class="sig">
    <div class="sig-line"></div>
    <div class="sig-label">${esc(label)}</div>
    <div class="sig-line" style="margin-top:34px; width:200px"></div>
    <div class="sig-label">Date signed</div>
  </div>`;
}


/** Renders the seal only when it is actually applied. Otherwise nothing at all. */
function sealSpot(ctx: SealContext): string {
  return sealBlockHtml(ctx);
}

export interface ApplicationBuildContext {
  associationName: string;
  officerRole: string;
  applicantName: string;
  applicantCityState?: string | null;
  decisionDate: string;
  certificateNumber: string;
  seatedOfficerCount: number | null;
  includeCertificate: boolean;
  /** True when the Association keeps a seal and the steward applied it. */
  sealEnabled?: boolean;
  /** The rendered seal markup. */
  sealSvg?: string;
}

export interface ApplicationOutput {
  key: string;
  title: string;
  html: string;
  signing: SigningMode;
  defaultInk?: boolean;
  signingReason?: string;
  
}

/** Used when the Association has fewer than three seated officers. */
export function buildPeoApprovalEntryHtml(ctx: ApplicationBuildContext): string {
  const inner = `
  <p>On ${esc(longDate(ctx.decisionDate))}, the ${esc(ctx.officerRole)} of ${esc(ctx.associationName)} made this entry in the record.</p>
  <h2>The decision</h2>
  <p>${esc(ctx.applicantName)}${ctx.applicantCityState ? ` of ${esc(ctx.applicantCityState)}` : ''} applied for membership. The application was read in full. The applicant is admitted to membership in ${esc(ctx.associationName)} as of ${esc(longDate(ctx.decisionDate))}.</p>
  <h2>What follows</h2>
  <ul>
    <li>The new member is entered on the private roster of the Association.</li>
    <li>The new member reads the Doctrine and the Covenant at the next sitting and signs the attestation there.</li>
    <li>This entry is kept with the records of the Association.</li>
  </ul>
  ${sigBlock(`${ctx.officerRole}, for ${ctx.associationName}`)}
  ${sealSpot(ctx)}`;
  return page('Approval Entry', 'A sole officer approval, entered in the governance log', inner);
}

/** Used when the Association has three or more seated officers. */
export function buildBoardApprovalResolutionHtml(ctx: ApplicationBuildContext): string {
  const inner = `
  <p>On ${esc(longDate(ctx.decisionDate))}, the Board of ${esc(ctx.associationName)} sat and decided this matter.</p>
  <h2>The decision</h2>
  <p>${esc(ctx.applicantName)}${ctx.applicantCityState ? ` of ${esc(ctx.applicantCityState)}` : ''} applied for membership. The application was read in full. The Board admits the applicant to membership in ${esc(ctx.associationName)} as of ${esc(longDate(ctx.decisionDate))}.</p>
  <h2>What follows</h2>
  <ul>
    <li>The new member is entered on the private roster of the Association.</li>
    <li>The new member reads the Doctrine and the Covenant at the next sitting and signs the attestation there.</li>
    <li>This resolution is kept with the records of the Association.</li>
  </ul>
  ${sigBlock(`${ctx.officerRole}, for ${ctx.associationName}`)}
  ${sigBlock('Board member')}
  ${sigBlock('Board member')}
  ${sealSpot(ctx)}`;
  return page('Board Approval Resolution', 'A decision of the Board, entered in the record', inner);
}

/** Two lines. Portal signable. */
export function buildNoticeOfAdmissionHtml(ctx: ApplicationBuildContext): string {
  const inner = `
  <p>Dear ${esc(ctx.applicantName)}, the Officers of ${esc(ctx.associationName)} confirm your admission into Membership as of ${esc(longDate(ctx.decisionDate))}.</p>
  <p>Welcome. Your onboarding begins this month.</p>
  ${sigBlock(`${ctx.officerRole}, for ${ctx.associationName}`)}`;
  return page('Notice of Admission', 'A short notice for the record', inner);
}

/** Signed by hand at the sitting. */
export function buildMembershipAttestationHtml(ctx: ApplicationBuildContext): string {
  const inner = `
  <p>I, ${esc(ctx.applicantName)}, having read the Doctrine of Divine Stewardship (Appendix B) and the Covenant of Privacy, Loyalty, and Protection (Appendix C) of ${esc(ctx.associationName)}, attest that I share the conviction the Doctrine states, that I enter Membership voluntarily under the Association's Constitution, and that I accept and will keep the Covenant of Privacy, Loyalty, and Protection in full.</p>
  ${sigBlock('New member signature (blue ink)')}
  <h2>Witnessed by</h2>
  <div class="sig">
    <div class="sig-line"></div>
    <div class="sig-label">${esc(ctx.officerRole)}, ${esc(ctx.associationName)}</div>
  </div>
  ${sealSpot(ctx)}`;
  return page('Membership Attestation', 'Signed by hand at the sitting', inner);
}

/** A dignified landscape certificate page. */
export function buildMemberCertificateHtml(ctx: ApplicationBuildContext): string {
  const style = `
  @page { size: letter landscape; margin: 0.6in; }
  body { font-family: Cambria, Georgia, serif; color: #1a1a1a; text-align: center; }
  .frame { position: relative; border: 3px double #7a6a4f; padding: 40px 56px; height: 6.4in; display: flex; flex-direction: column; justify-content: center; }
  .assoc { font-size: 20pt; text-transform: uppercase; letter-spacing: 0.14em; margin-bottom: 6px; }
  .kicker { font-size: 11pt; font-style: italic; color: #5a5348; margin-bottom: 34px; }
  .name { font-size: 30pt; margin: 10px 0 6px; }
  .line { font-size: 13pt; margin-bottom: 4px; }
  .certno { margin-top: 26px; font-size: 11pt; letter-spacing: 0.08em; }
  .sig-line { border-bottom: 1px solid #333; width: 300px; margin: 46px auto 6px; height: 30px; }
  .sigs { display: flex; justify-content: center; gap: 40px; }
  .sig-col { flex: 0 1 300px; }
  .sig-col .sig-line { width: 100%; }
  .sig-label { font-size: 10pt; }
  .footer { margin-top: 28px; font-size: 10pt; font-style: italic; color: #5a5348; }
  .draft { border: 1px solid #8a6d3b; background: #fdf6e3; color: #6b5320; padding: 8px 12px; margin-bottom: 14px; font-size: 9pt; letter-spacing: 0.04em; }
  .cert-seal { position: absolute; left: 0.9in; bottom: 0.9in; }
  ${SEAL_STYLE_CSS}
  `;
  return `<!DOCTYPE html>
<html lang="en">
<head><meta charset="UTF-8"><title>Member Certificate</title><style>${style}</style></head>
<body>
  ${APPLICATION_FORMATS_APPROVED ? '' : DRAFT_BANNER}
  <div class="frame">
    <div class="assoc">${esc(ctx.associationName)}</div>
    <div class="kicker">Certificate of Membership</div>
    <div class="line">This certifies that</div>
    <div class="name">${esc(ctx.applicantName)}</div>
    <div class="line">was received into Membership on ${esc(longDate(ctx.decisionDate))}, having attested to the Doctrine of Divine Stewardship and the covenants of this Association, and is enrolled among its members with all the fellowship, care, and dignity that Membership carries.</div>
    <div class="certno">Certificate ${esc(ctx.certificateNumber)}</div>
    <div class="sigs">
      <div class="sig-col">
        <div class="sig-line"></div>
        <div class="sig-label">${esc(ctx.officerRole)}</div>
      </div>
      <div class="sig-col">
        <div class="sig-line"></div>
        <div class="sig-label">Witness Officer</div>
      </div>
    </div>
    ${ctx.sealEnabled && ctx.sealSvg ? `<div class="cert-seal">${sealBlockHtml(ctx)}</div>` : ''}
    <div class="footer">Recorded on the private roster of the Association.</div>
  </div>
</body>
</html>`;
}

function buildApprovalOutputsInternal(ctx: ApplicationBuildContext): ApplicationOutput[] {
  const boardSeated = (ctx.seatedOfficerCount ?? 0) >= 3;
  const outputs: ApplicationOutput[] = [];

  outputs.push(
    boardSeated
      ? {
          key: 'application_board_approval_resolution',
          title: 'Board Approval Resolution',
          html: buildBoardApprovalResolutionHtml(ctx),
          signing: 'stewards_choice',
          defaultInk: true,
        }
      : {
          key: 'application_peo_approval_entry',
          title: 'Approval Entry',
          html: buildPeoApprovalEntryHtml(ctx),
          signing: 'stewards_choice',
          defaultInk: true,
        },
  );

  outputs.push({
    key: 'application_notice_of_admission',
    title: 'Notice of Admission',
    html: buildNoticeOfAdmissionHtml(ctx),
    signing: 'stewards_choice',
    defaultInk: false,
  });

  outputs.push({
    key: 'application_membership_attestation',
    title: 'Membership Attestation',
    html: buildMembershipAttestationHtml(ctx),
    signing: 'stewards_choice',
    defaultInk: true,
    signingReason:
      'This one signs in blue ink at the sitting where the new member reads the Doctrine and the Covenant. Print it, sign it, scan it to the Record.',
  });

  if (ctx.includeCertificate) {
    outputs.push({
      key: 'application_member_certificate',
      title: `Member Certificate ${ctx.certificateNumber}`,
      html: buildMemberCertificateHtml(ctx),
      signing: 'stewards_choice',
      defaultInk: true,
    });
  }

  return outputs;
}

/**
 * Every generated paper carries only the entity's identity. The HTML title
 * becomes "Document title, Entity name" so the file and the PDF metadata name
 * the Association, not the portal.
 */
export function buildApprovalOutputs(ctx: ApplicationBuildContext): ApplicationOutput[] {
  const entityName = ctx.associationName;
  return buildApprovalOutputsInternal(ctx).map((output) => ({ ...output, html: withEntityTitle(output.html, entityName) }));
}
